import { InfoCircledIcon } from '@radix-ui/react-icons'
import { useQueryClient } from '@tanstack/react-query'
import { FoldVerticalIcon, SparklesIcon, TriangleAlertIcon } from 'lucide-react'
import { Fragment, useMemo, useState } from 'react'

import { Badge, Banner, BorderedCheckbox, Button, CheckboxGroup, Tooltip } from 'slab'
import { cn } from 'utils'
import {
  getGetQuotingSmartCensusParseJobsIdQueryKey,
  useGetQuotingSmartCensusParseJobsIdInputFileContents as useGetInputFileContents,
  usePutQuotingSmartCensusParseJobsIdUpdateRelationshipValuesToConsiderEmployee as useUpdateRelationshipValuesToConsiderEmployee
} from 'utils/web_api'
import { type QuotingSmartCensusParseJob } from 'utils/web_api.schemas'

export default function DefinerelationshipValuesToConsiderEmployeeView ({ job }: { job: QuotingSmartCensusParseJob }) {
  const queryClient = useQueryClient()
  const updateRelationshipValuesToConsiderEmployeeMutation = useUpdateRelationshipValuesToConsiderEmployee()

  const [hoveredEmpoyeeRelationshipValue, setHoveredEmpoyeeRelationshipValue] = useState<string | null>(null)
  const [selectedEmpoyeeRelationshipValues, setSelectedEmpoyeeRelationshipValues] = useState<string[]>([])

  // because CADE has not yet sorted and formatted the input file, we need to get the raw input file contents
  // which may not have headers defined in the expected places, so we just brute force find rows of interest by iterating through
  const { data, isLoading: isLoadingInputFileContents } = useGetInputFileContents(job.id, { raw_input_file: true }, {
    query: {
      retry: (_failureCount, error) => {
        // 404 means the file has not yet been created, but should become attached at some point during the background job
        return ((error as any)?.status === 404)
      },
    },
  })

  const exampleRows = useMemo(() => (
    // get one row that has any value matching a detected employee relationship value, and attach the row number
    job.detected_employee_relationship_values.map(relationshipValue => {
      const rowIndexWithRelationshipValue = data?.rows.findIndex(row => Object.values(row).includes(relationshipValue))
      const rowWithRelationshipValue = typeof rowIndexWithRelationshipValue === 'number' && rowIndexWithRelationshipValue !== -1 ? data?.rows[rowIndexWithRelationshipValue] : null
      if (rowIndexWithRelationshipValue !== -1 && rowWithRelationshipValue) {
        return { row: Object.values(rowWithRelationshipValue), rowIndex: rowIndexWithRelationshipValue }
      }
      return null
    }).filter((item): item is { row: string[], rowIndex: number } => item !== null)
  ), [data, job.detected_employee_relationship_values])

  const handleUpdateRelationshipValuesToConsiderEmployee = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const formData = new FormData(event.target as HTMLFormElement)
    const relationshipValuesToConsiderEmployee = formData.getAll('relationship_values_to_consider_employee')
    updateRelationshipValuesToConsiderEmployeeMutation.mutate({
      id: job.id,
      data: { relationship_values_to_consider_employee: relationshipValuesToConsiderEmployee },
    }, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetQuotingSmartCensusParseJobsIdQueryKey(job.id) })
      },
    })
  }

  return (
    <>
      <Banner type='warning' title='Please define the employee relationship values for the census' icon={TriangleAlertIcon}>
        <p>CADE was unable to detect which values indicate a row should be considered an 'employee'.</p>
      </Banner>
      {isLoadingInputFileContents
        ? (
          <div className='flex justify-center items-center h-44 rounded-md border border-gray-200 p-4 animate-pulse bg-gray-200' />
          )
        : (
          <div className='bg-white rounded-md border border-gray-200 overflow-x-scroll'>
            <h6 className='text-sm text-gray-700 p-4 px-2'>Example rows:</h6>
            <table>
              <thead className='sticky top-0'>
                <tr className='bg-gray-100'>
                  <th className='border border-gray-200 p-0.5' />
                  <th className='border border-gray-200 py-1 px-2 text-center font-bold whitespace-nowrap'>
                    <div className='flex items-center justify-center'>
                      <span>Participant Type</span>
                      <Tooltip
                        content='Based on your selection, how CADE will determine the participant type for the row.'
                        delayDuration={0}
                      >
                        <InfoCircledIcon className='h-3 w-3 ml-1 cursor-help' />
                      </Tooltip>
                    </div>
                  </th>
                  {data?.headers.map((header, idx) => (
                    <th key={idx} className='border border-gray-200 py-1 px-2 text-center font-bold whitespace-nowrap'>
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {exampleRows.map((exampleRow, rowIdx) => {
                  const isSelected = exampleRow.row.some(value => selectedEmpoyeeRelationshipValues.includes(value))
                  const isHovered = hoveredEmpoyeeRelationshipValue !== null && exampleRow.row.some(value => hoveredEmpoyeeRelationshipValue === value)
                  const previousRowIndex = rowIdx === 0 ? 0 : exampleRows[rowIdx - 1].rowIndex
                  const showEllipsisBefore = rowIdx === 0 ? exampleRow.rowIndex > 1 : exampleRow.rowIndex > previousRowIndex + 1

                  return (
                    <Fragment key={rowIdx}>
                      {showEllipsisBefore && (
                        <tr className='text-gray-400'>
                          <td colSpan={2 + (data?.headers.length || 0)} className='border border-gray-100 py-1 px-2 bg-gray-50'>
                            <FoldVerticalIcon className='h-3 w-3 text-gray-700' />
                          </td>
                        </tr>
                      )}
                      <tr className={cn(isHovered && 'bg-gray-100')}>
                        <td className='border border-gray-100 py-1 px-2 text-center text-gray-500'>{exampleRow.rowIndex}</td>
                        <td className='border border-gray-100 py-1 px-2 text-center text-gray-500'>
                          <Badge
                            type={
                                  selectedEmpoyeeRelationshipValues.length === 0
                                    ? 'neutral'
                                    : isSelected ? 'success' : 'warning'
                                }
                          >
                            {selectedEmpoyeeRelationshipValues.length === 0 ? '?' : isSelected ? 'Employee' : 'Dependent'}
                          </Badge>
                        </td>
                        {exampleRow.row.map((value, valueIdx) => (
                          <td key={valueIdx} className='border border-gray-100 py-1 px-2 whitespace-nowrap'>{String(value)}</td>
                        ))}
                      </tr>
                    </Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>
          )}
      <form onSubmit={handleUpdateRelationshipValuesToConsiderEmployee}>
        <CheckboxGroup label='Select which values represent an employee "relationship"'>
          {job.detected_employee_relationship_values.map(relationshipValue => (
            <div
              key={relationshipValue}
              onMouseEnter={() => setHoveredEmpoyeeRelationshipValue(relationshipValue)}
              onMouseLeave={() => setHoveredEmpoyeeRelationshipValue(null)}
            >
              <BorderedCheckbox
                name='relationship_values_to_consider_employee'
                value={relationshipValue}
                label={relationshipValue}
                onChange={e => {
                  if (e.target.checked) {
                    setSelectedEmpoyeeRelationshipValues(prev =>
                      prev.includes(relationshipValue)
                        ? prev
                        : [...prev, relationshipValue]
                    )
                  } else {
                    setSelectedEmpoyeeRelationshipValues(prev =>
                      prev.filter(value => value !== relationshipValue)
                    )
                  }
                }}
              />
            </div>
          ))}
        </CheckboxGroup>
        <div className='flex mt-4'>
          <Button
            variant='primary'
            type='submit'
            rightIcon={SparklesIcon}
            disabled={updateRelationshipValuesToConsiderEmployeeMutation.isPending || selectedEmpoyeeRelationshipValues.length === 0}
          >
            Continue with census mapping
          </Button>
        </div>
      </form>
    </>
  )
}
